import { Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder } from '@angular/forms';
import { PaginatePipeArgs } from 'ngx-pagination';
import { ApiDocument, DocumentCategory, DocumentInput, DocumentOut, EfektikaActiveOut, PagedResultDocumentOut } from 'src/app/services/rest-api/rest-api';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { UserService } from 'src/app/services/user/user.service';
import { saveAs } from 'file-saver';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import * as dayjs from 'dayjs';
import { MODAL_BODY_CLASS } from 'src/app/services/modal/modal/modal.component';
import { LoggedUser } from 'src/app/services/authentication/authentication';
import { Router } from '@angular/router';
import { ToastNotificationComponent } from 'src/app/shared/toast-notification/toast-notification.component';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs/operators';
import Decimal from 'decimal.js';
import { Browser } from '@capacitor/browser';
import { Subscription } from 'rxjs';
import { EFEKTIKA_PDF, DATE_OF_OPF_TRANSFERS } from 'src/app/shared/constants';
import { NativeCheckService } from 'src/app/services/native-check.service';

export enum AvailableUiCategories {
  LISTINGS = 'LISTINGS',
  OTHER = 'OTHER',
}

interface StaticDocument {
  name: string,
  translateKey: string,
  creationDate?: string, // if set document filtration date is set to this Date
  order?: number | null,
};

interface UniqueDocument {
  // used as filename
  // without .pdf - its added in code
  name: string,
  // any dayjs input
  // if client is created after, display current client create date
  creationDate: string,
  dipUserOnly?: boolean,
  latest?: boolean,
  validFrom?: string | null,
  validTo?: string | null,
  order?: number | null,
  contractConditions?: ContractConditions,
  userCreatedBefore?: string,
  creationDateConditions?: CreationDateConditions,
};

interface CreationDateConditions {
  opfContractsBasedPriceList: boolean,
}

interface ContractConditions {
  includeOpf: boolean,
}

enum UniqueDocumentType {
  VOP,
  DIP_LEGAL_REQUIREMENTS,
  ACCESSIBILITY_OF_SERVICES,
  SERVICE_PRICE_LIST
}

interface UniqueDocumentGroup {
  type: UniqueDocumentType,
  documents: UniqueDocument[],
  omitOlderForNewUser: boolean,
}

const staticDocuments: string[] = [
  'Disclaimer_rizikovosti',
  'Informace_o_rizeni_stretu_zajmu',
  'Informace_a_sdeleni_pro_zakazniky',
  'Informace_o_pravidlech_odmenovani',
  'Informace_o_investicnich_sluzbach_investičních_nástrojích_a_rizicich_spojenych_s_investovanim_do_techto_investicnich_nastroju',
  'Pravidla_pro_provadeni_obchodu',
  'Souhrnne_predsmluvni_informace_spolecnosti_Broker_Consulting',
  'Prohlaseni_o_ochrane_osobnich_udaju_a_souboru_cookie',
  'Podminky_uzivani_stranek_MIS_2023',
  'Informace_o_zpracovani_osobnich_udaju_klientu_MIS-BWM',
];

// special documents
// EfektikaPdf (imported)
const dipDocumentName = 'Obchodni_podminky_DIP_02_2024';

const uniqueDocumentGroups: UniqueDocumentGroup[] = [
  {
    type: UniqueDocumentType.SERVICE_PRICE_LIST,
    documents: [
      {
        name: 'Sazebnik_sluzeb',
        creationDate: '2023-10-27',
        order: 1,
        userCreatedBefore: DATE_OF_OPF_TRANSFERS,
      },
      {
        name: 'Sazebnik_sluzeb_1.9.2025',
        creationDate: DATE_OF_OPF_TRANSFERS,
        order: 2,
        contractConditions: {
          includeOpf: true,
        },
        creationDateConditions: {
          opfContractsBasedPriceList: true,
        }
      },
    ],
    omitOlderForNewUser: false,
  },
  {
    type: UniqueDocumentType.DIP_LEGAL_REQUIREMENTS,
    documents: [
      {
        name: 'Prohlaseni_ke_splneni_zakonnych_podminek_pro_DIP',
        creationDate: '2025-04-29',
        dipUserOnly: true,
      }, // additional DIP Q2/25,
    ],
    omitOlderForNewUser: false,
  },
  {
    type: UniqueDocumentType.ACCESSIBILITY_OF_SERVICES,
    documents: [
      {
        name: 'Informace_o_plneni_pozadavku_na_pristupnost_sluzeb_spolecnosti_MONECO_investicni_společnost_as',
        creationDate: '2025-06-28',
        latest: true,
      },
    ],
    omitOlderForNewUser: true,
  },
  {
    type: UniqueDocumentType.VOP,
    documents: [
      {
        name: 'Vseobecne_obchodni_podminky',
        creationDate: '2024-10-31', // to always fallback to user created date
        validTo: '2024-11-30',
        validFrom: null,
        latest: false,
        order: 1,
      },
      {
        name: 'Vseobecne_obchodni_podminky_1.12.2024',
        creationDate: '2024-11-01',
        validFrom: '2024-12-01',
        validTo: null,
        latest: false,
        order: 2,
      },
      {
        name: 'Vseobecne_obchodni_podminky_1.9.2025',
        creationDate: '2025-08-01',
        validFrom: DATE_OF_OPF_TRANSFERS,
        validTo: null,
        latest: true,
        order: 3,
      },
    ],
    omitOlderForNewUser: true,
  },
];

@Component({
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss'],
  standalone: false
})
export class DocumentsComponent implements OnInit, OnDestroy {
  public paginationConfig: PaginatePipeArgs = {
    itemsPerPage: 10,
    currentPage: 1,
    id: 'available-documents-pagination'
  };

  public availableUiCategories = AvailableUiCategories;
  public categorySelected: AvailableUiCategories = AvailableUiCategories.OTHER;

  @ViewChild('datepicker', {read:ElementRef}) public datepicker?: ElementRef<HTMLInputElement>;

  public dateFrom: FormControl<dayjs.Dayjs | null> = new FormControl<dayjs.Dayjs>(dayjs(this.loggedUser.createdDate));
  public dateTo: FormControl<dayjs.Dayjs | null> = new FormControl<dayjs.Dayjs>(dayjs().add(1, 'month'));

  public searchName: FormControl<string | null> = new FormControl<string>('');
  private get searchValue(): string {
    return this.searchName.value ?? '';
  }

  public documents: ApiDocument[] = [];
  public allSearchableDocuments: {
    listings: DocumentOut[],
    other: DocumentOut[],
  } = {
    listings: [],
    other: [],
  };

  public phoneFiltersToggled = false;
  private subscriptions: Subscription[] = [];

  private availableStaticDocuments: StaticDocument[] = [];
  private filteredStaticDocuments: StaticDocument[] = [];

  private getSelectedCategories(): DocumentCategory[] {
    if(this.categorySelected === AvailableUiCategories.LISTINGS) {
      return [DocumentCategory.REPORT];
    } else {
      return [DocumentCategory.ACKNOWLEDGEMENT, DocumentCategory.OTHER];
    }
  }

  public preloader = true;
  public searchApiPreloader = true;
  public apiError = false;
  public searchApiDataError = false;

  public generatingLinkPreloader = false;

  private get loggedUser(): LoggedUser {
    if(!this.userService.loggedUser || this.userService.loggedUser === null) {
      this.router.navigate(['/login']);
    }

    return this.userService.loggedUser!;
  }

  public constructor(
    private fb: UntypedFormBuilder,
    public toastService: ToastNotificationService,
    public userService: UserService,
    private restApiService: RestApiService,
    private loggerService: LoggerService,
    private router: Router,
    private renderer: Renderer2,
    private translateService: TranslateService,
    private nativeCheckService: NativeCheckService,
  ) { }

  public ngOnInit(): void {
    this.restApiService.efektikaValidSince().subscribe({
      next: (data) => this.assignAllStaticDocuments(data),
      error: (err) => this.handleEfektikaValidityError(err),
    });
    this.initFormValueSubscribe();
    this.loadAllForSearch();
  }

  private handleEfektikaValidityError(error: unknown): void {
    this.loggerService.error(error);
    this.toastService.addNotification({
      title: 'toastNotifications.headers.error',
      message: 'toastNotifications.messages.getDocumentsEfektikaError',
      type: 'warning',
      component: ToastNotificationComponent
    });
    // if efektika fetch fail, still pass at least other static documents
    this.assignAllStaticDocuments();
  }

  private loadAllForSearch(): void {
    this.restApiService.getDocuments().subscribe({
      error: this.handleInitDocumentsSearchError.bind(this),
      next: this.handleInitDocumentsSearchData.bind(this),
    });
  }

  private handleInitDocumentsSearchError(error: unknown): void {
    this.searchApiPreloader = false;
    this.searchApiDataError = true;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.addNotification({
      title: 'toastNotifications.headers.error',
      message: 'toastNotifications.messages.getDocumentsSearchError',
      type: 'warning',
      component: ToastNotificationComponent
    });
  }

  private handleInitDocumentsSearchData(data: PagedResultDocumentOut): void {
    this.searchApiPreloader = false;
    this.searchApiDataError = false;

    this.allSearchableDocuments.other.push(...data.content.filter(document => document.category === DocumentCategory.ACKNOWLEDGEMENT || document.category === DocumentCategory.OTHER));
    this.allSearchableDocuments.listings.push(...data.content.filter(document => document.category === DocumentCategory.REPORT));
  }

  public searchByString(): void {
    const issuedFrom = dayjs(this.dateFrom.value).startOf('month');
    const issuedTo = dayjs(this.dateTo.value).endOf('month');

    const searchableDocuments = this.categorySelected === AvailableUiCategories.LISTINGS ? this.allSearchableDocuments.listings : this.allSearchableDocuments.other;
    // static documents search done before push
    const filteredDocuments = searchableDocuments
      .filter(loopedDocument => {
        return loopedDocument.description.toLowerCase().includes(this.searchValue.trim().toLowerCase()) &&
          dayjs(loopedDocument.issuedAt).isBefore(issuedTo) &&
          dayjs(loopedDocument.issuedAt).isAfter(issuedFrom)
      });

    this.preloader = true;

    this.documents.length = 0;
    this.paginationConfig.currentPage = 1;

    this.handleDocumentsData({
      content: filteredDocuments,
      totalCount: filteredDocuments.length,
    });
  }

  private loadDynamicDocuments(resetPage?: true): void {
    const issuedFrom = dayjs(this.dateFrom.value).startOf('month').format('YYYY-MM-DD');
    const issuedTo = dayjs(this.dateTo.value).endOf('month').format('YYYY-MM-DD');

    if(resetPage) {
      this.paginationConfig.currentPage = 1;
    }

    const params: DocumentInput = {
      page: parseInt(this.paginationConfig.currentPage + '') - 1, // API paginated from 0
      size: this.paginationConfig.itemsPerPage,
      issuedFrom: issuedFrom,
      issuedTo: issuedTo,
      category: this.getSelectedCategories(),
    }

    this.documents.length = 0;

    if(this.getSelectedCategories().length) {
      this.preloader = true;
      this.restApiService.getDocuments(params).subscribe({
        error: this.handleDocumentsError.bind(this),
        next: this.handleDocumentsData.bind(this),
      });
    }
  }

  private handleDocumentsError(error: unknown): void {
    this.preloader = false;
    this.apiError = true;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  private handleDocumentsData(data: PagedResultDocumentOut): void {
    this.apiError = false;

    for (const apiDocument of data.content) {
    if(
      this.getSelectedCategories().includes(apiDocument.category) &&
      apiDocument.description.toLowerCase().includes(this.searchValue.trim().toLowerCase())
    ) {
        this.documents.push({
          type: apiDocument.category.toLowerCase() as 'confirmation' | 'listing' | 'other',
          date: apiDocument.issuedAt,
          name: apiDocument.description,
          documentId: apiDocument.documentId,
        })
      }
    }

    this.paginationConfig.totalItems = data.totalCount;
    this.handleStaticDocuments();

    this.preloader = false;
  }

  private handleStaticDocuments() {
    // static documents should be 'other' category
    if (this.categorySelected === AvailableUiCategories.OTHER) {
      this.filteredStaticDocuments = this.availableStaticDocuments
      .filter(pdfDoc =>
        pdfDoc.name.toLowerCase().includes(this.searchValue.trim().toLowerCase()) &&
        this.creationDateInRange(pdfDoc.creationDate ?? this.loggedUser.createdDate)
      );

      this.pushStaticDocuments();

      this.paginationConfig.totalItems = Decimal.sum(this.filteredStaticDocuments.length, this.paginationConfig.totalItems ?? 0).toNumber();
      this.documents.sort((a, b) => {
        if (a.order && b.order) return b.order - a.order;
        else return dayjs(b.date).diff(dayjs(a.date));
      });
    }
  }

  private creationDateInRange(checkedDate: string): boolean {
    return (
      (
        dayjs(this.dateFrom.value).isBefore(dayjs(checkedDate)) &&
        dayjs(this.dateTo.value).isAfter(dayjs(checkedDate))
      ) ||
      (
        dayjs(this.dateFrom.value).isSame(dayjs(checkedDate)) ||
        dayjs(this.dateTo.value).isSame(dayjs(checkedDate))
      )
    )
  }

  private addConditionallyUniqueDocumentToCustomizedDocuments(
    uniqueDocument: UniqueDocument, customizedStaticDocuments: StaticDocument[], oldest: boolean
  ) {
    if(
      !uniqueDocument.dipUserOnly ||
      (uniqueDocument.dipUserOnly && this.loggedUser.validContracts.some(contract => contract.dip))
    ) {
      if(uniqueDocument.creationDateConditions?.opfContractsBasedPriceList) {
        const parsedOpfValidDocument = this.handleOpfContractsBasedDocument(uniqueDocument);
        if(parsedOpfValidDocument) {
          customizedStaticDocuments.push(parsedOpfValidDocument);
        }
      } else {
        customizedStaticDocuments.push(this.handleDocumentWithUniqueDate(uniqueDocument, oldest));
      }
    }
  }

  private getLatestBeforeUserCreated(
    uniqueDocuments: UniqueDocument[],  userCreatedDate: dayjs.Dayjs
  ): dayjs.Dayjs {
    let latestBeforeUserCreated = dayjs('2000-01-01');
    for (const uniqueDocument of uniqueDocuments) {
      const documentValidFrom = dayjs(uniqueDocument.validFrom);
    if (
      (documentValidFrom.isBefore(userCreatedDate) ||
        documentValidFrom.isSame(userCreatedDate, 'day')) &&
      documentValidFrom.isAfter(latestBeforeUserCreated)
    )
      latestBeforeUserCreated = documentValidFrom;
    }
    return latestBeforeUserCreated;
  }

  /**
  * If omitOlderForNewUser = true, documents are added/omitted based on user createdAt date and document createdAt date.
  *
  * example:
  *
  * - 4 documents of the same group created: 1.3., 1.4., 1.6., 1.9.
  * - user created date:
  *  # 1.1.  => documents 1.3., 1.4., 1.6., 1.9. (no omitting)
  *  # 1.5.  => documents 1.4.*, 1.6., 1.9. (latest before user created is 1.4., omitting 1.3.)
  *  # 1.10. => document  1.9.* (latest before user created is 1.9., omitting 1.3., 1.4., 1.6.)
  *
  * - documents marked with * will have date of the document adjusted to user creation date
  *
  * @returns customized static documents
  */
  private getCustomizedStaticDocuments(): StaticDocument[] {
    const customizedStaticDocuments: StaticDocument[] = [];

    for (const uniqueDocumentGroup of uniqueDocumentGroups) {
      if (!uniqueDocumentGroup.omitOlderForNewUser) {
        for (const uniqueDocument of uniqueDocumentGroup.documents) {
          if(
            this.userCreatedBefore(uniqueDocument.userCreatedBefore) &&
            this.additionsValidForDocument(uniqueDocument.contractConditions)
          ) {
            this.addConditionallyUniqueDocumentToCustomizedDocuments(
              uniqueDocument, customizedStaticDocuments,
              true
            );
          }
        }
      } else {
        const latestDocument = uniqueDocumentGroup.documents.find(doc => doc.latest)!;
        const latestDate = dayjs(latestDocument.validFrom);
        const userCreatedDate = dayjs(this.loggedUser.createdDate);

        // user createdAt > latest ==> add only latest document
        if (userCreatedDate.isAfter(latestDate)) {
          this.addConditionallyUniqueDocumentToCustomizedDocuments(
            latestDocument, customizedStaticDocuments, true
          );
          // user createdAt < latest ==> add documents from nearest before user created till newest
        } else {
          const latestBeforeUserCreated = this.getLatestBeforeUserCreated(
            uniqueDocumentGroup.documents, userCreatedDate
          );
          const oldestDocumentValidFrom = uniqueDocumentGroup.documents.map(
            doc => dayjs(doc.validFrom)
          ).filter(date => date.isValid()).reduce((min, d) => d.isBefore(min) ? d : min);

          const oldestDocumentValidTo = uniqueDocumentGroup.documents.map(
            doc => dayjs(doc.validTo)
          ).filter(date => date.isValid()).reduce((min, d) => d.isBefore(min) ? d : min);

          for (const uniqueDocument of uniqueDocumentGroup.documents) {

            const documentValidFrom = dayjs(uniqueDocument.validFrom); // valid false for null
            const documentValidTo = dayjs(uniqueDocument.validTo); // valid false for null

            if (
              (documentValidTo.isValid() &&
                (documentValidTo.isAfter(userCreatedDate) ||
                  documentValidTo.isSame(userCreatedDate))) ||
              (documentValidFrom.isValid() &&
                (documentValidFrom.isSame(latestBeforeUserCreated) ||
                  documentValidFrom.isAfter(latestBeforeUserCreated)))
            ) {
              this.addConditionallyUniqueDocumentToCustomizedDocuments(
                uniqueDocument,
                customizedStaticDocuments,
                // is oldest date
                (documentValidTo.isSame(oldestDocumentValidTo) &&
                  oldestDocumentValidTo.isBefore(oldestDocumentValidFrom)) ||
                  (documentValidFrom.isSame(oldestDocumentValidFrom) &&
                    oldestDocumentValidFrom.isBefore(oldestDocumentValidTo)),
              );
            }
          }
        }
      }
    }

    return customizedStaticDocuments;
  }

  private additionsValidForDocument(conditions?: ContractConditions): boolean {
    if(
      !conditions ||
      conditions &&
      (!conditions.includeOpf || this.loggedUser.validContracts.find(contract => contract.opfFeeImplementation))
    ) {
      return true;
    }
    return false;
  }

  private userCreatedBefore(date?: string): boolean {
    if(
      !date ||
      date && dayjs(this.loggedUser.createdDate).isBefore(dayjs(date))
    ) {
      return true;
    }
    return false;
  }

  private assignAllStaticDocuments(efektikaValid?: EfektikaActiveOut): void {
    const customizedStaticDocuments: StaticDocument[] = this.getCustomizedStaticDocuments();

    if(this.loggedUser.validContracts.some(contract => contract.dip)) {
      customizedStaticDocuments.push({ translateKey: dipDocumentName, name: this.translateService.instant('documents.staticFiles.' + dipDocumentName), });
    }

    if(efektikaValid && efektikaValid.activeSince !== null) {
      customizedStaticDocuments.push({
        translateKey: EFEKTIKA_PDF,
        name: this.translateService.instant('documents.staticFiles.' + EFEKTIKA_PDF),
        creationDate: efektikaValid.activeSince
      });
    }

    this.availableStaticDocuments = [...customizedStaticDocuments, ...staticDocuments.map(staticDoc => {return {
      name: this.translateService.instant('documents.staticFiles.' + staticDoc),
      translateKey: staticDoc,
      creationDate: this.loggedUser.createdDate,
    }})];

    this.loadDynamicDocuments();
  }

  private handleDocumentWithUniqueDate(uniqueDocument: UniqueDocument, oldest: boolean): StaticDocument {
    if(dayjs(this.loggedUser.createdDate).isBefore(dayjs(uniqueDocument.creationDate)) && !oldest) {
      return this.createDocumentWithCustomCreationDate(uniqueDocument, uniqueDocument.creationDate);
    } else {
      return this.createDocumentWithCustomCreationDate(uniqueDocument, this.loggedUser.createdDate);
    }
  }

  private handleOpfContractsBasedDocument(uniqueDocument: UniqueDocument): StaticDocument | void {
    const afterOpfContract = this.loggedUser.validContracts.find(contract => dayjs(contract.invSignDate).isAfter(DATE_OF_OPF_TRANSFERS));

    if(!this.loggedUser.opfTransferAt && !afterOpfContract) {
      return;
    }

    if(!this.loggedUser.opfTransferAt && afterOpfContract) {
      return this.createDocumentWithCustomCreationDate(uniqueDocument, afterOpfContract.invSignDate);
    }
    if(this.loggedUser.opfTransferAt && !afterOpfContract) {
      return this.createDocumentWithCustomCreationDate(uniqueDocument, this.loggedUser.opfTransferAt);
    }

    if(this.loggedUser.opfTransferAt && afterOpfContract) {
      const soonerDate = dayjs(afterOpfContract.invSignDate).isBefore(this.loggedUser.opfTransferAt) ?
      afterOpfContract.invSignDate :
      this.loggedUser.opfTransferAt;
      return this.createDocumentWithCustomCreationDate(uniqueDocument, soonerDate);
    }

    return;
  }

  private createDocumentWithCustomCreationDate(uniqueDocument: UniqueDocument, customDate: string): StaticDocument {
    return {
      translateKey: uniqueDocument.name,
      name: this.translateService.instant('documents.staticFiles.' + uniqueDocument.name),
      creationDate: customDate,
      order: uniqueDocument.order,
    };
  }

  public prevPageAvailable(): boolean {
    const currentPageNumber = this.paginationConfig.currentPage ? +this.paginationConfig.currentPage : 0;
    return currentPageNumber > 1;
  }

  public nextPageAvailable(): boolean {
    const moreItemsAvailable = (
      (this.paginationConfig.totalItems as number) -
      (((this.paginationConfig.currentPage as number)) * (this.paginationConfig.itemsPerPage as number))
    ) > 0;
    return moreItemsAvailable;
  }

  public loadPrevPage(): void {
    this.paginationConfig.currentPage = (this.paginationConfig.currentPage as number) - 1;
    this.loadDynamicDocuments();
  }

  public loadNextPage(): void {
    this.paginationConfig.currentPage = (this.paginationConfig.currentPage as number) + 1;
    this.loadDynamicDocuments();
  }

  public goToPage(page: number): void {
    this.paginationConfig.currentPage = page;
    this.loadDynamicDocuments();
  }

  private pushStaticDocuments(): void {
    const startIndex = Decimal.mul(Decimal.sum(this.paginationConfig.currentPage!, - 1), this.paginationConfig.itemsPerPage!);
    const endIndex = Decimal.min(Decimal.sum(startIndex, this.paginationConfig.itemsPerPage!), this.paginationConfig.totalItems!);
    const currentPageItemCount = endIndex.toNumber() - startIndex.toNumber();
    const toPush = Decimal.sum(this.paginationConfig.itemsPerPage!, -currentPageItemCount).toNumber();

    if(currentPageItemCount > 0) {
      for (let index = 0; index < toPush; index++) {
        if(this.filteredStaticDocuments[index]?.name.toLowerCase().includes(this.searchValue.trim().toLowerCase())) {
          this.documents.push(this.createStaticDocument(this.filteredStaticDocuments[index]));
        }
      }
    } else {
      const offset = Decimal.abs(currentPageItemCount).toNumber();
      const itemsPerPageNumber = this.paginationConfig.itemsPerPage ? +this.paginationConfig.itemsPerPage : 0;
      for (let index = offset; this.documents.length < itemsPerPageNumber && index < this.filteredStaticDocuments.length; index++) {
        if(this.filteredStaticDocuments[index]?.name.toLowerCase().includes(this.searchValue.trim().toLowerCase())) {
          this.documents.push(this.createStaticDocument(this.filteredStaticDocuments[index]))
        }
      }
    }
  }

  private createStaticDocument(staticDocument: StaticDocument): ApiDocument {
    return {
      type: 'other',
      date: staticDocument.creationDate ?? this.loggedUser.createdDate,
      name: staticDocument.name,
      documentId: NaN,
      fileUrl: '/assets/documents/' + staticDocument.translateKey + '.pdf',
      order: staticDocument.order,
    };
  }

  private initFormValueSubscribe(): void {
    this.subscriptions.push(this.dateFrom.valueChanges.pipe(debounceTime(10)).subscribe( () => this.loadDynamicDocuments(true) ));
    this.subscriptions.push(this.dateTo.valueChanges.pipe(debounceTime(10)).subscribe( () => this.loadDynamicDocuments(true) ));
    this.subscriptions.push(this.searchName.valueChanges.pipe(debounceTime(10)).subscribe( () => this.searchByString() ));
  }

  public saveFile(file: ApiDocument): void {
    if(isNaN(file.documentId)) {
      //static
      if(this.nativeCheckService.isNativePlatform()) {
        // FIXME - source of static documents should be from /src/assets/documents, not hardcoded production (1)
        const productionUrl = 'https://bwm.bcas.cz/';
        this.handleDownload(file.name, productionUrl + file.fileUrl);

      } else {
        this.handleDownload(file.name + '.pdf', file.fileUrl!);
      }
    } else {
      this.generatingLinkPreloader = true;
      this.renderer.addClass(document.body, MODAL_BODY_CLASS);

      this.restApiService.generateDocumentLink(file.documentId).subscribe({
        error: this.handleDocumentsUrlError.bind(this),
        next: (data) => this.handleDownload(file.name, data.link),
      });
    }
  }

  private handleDocumentsUrlError(error: unknown): void {
    this.generatingLinkPreloader = false;
    this.renderer.removeClass(document.body, MODAL_BODY_CLASS);
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  private handleDownload(filename: string, url: string): void {
    if(this.nativeCheckService.isNativePlatform()) {
      Browser.open({ url: url });
    } else {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';

      xhr.onload = () => {
        if (xhr.status === 200) {
          const blob = new Blob([xhr.response], { type: 'application/pdf' });
          saveAs(blob, filename);
        } else {
          this.toastService.addNotification({
            title: 'toastNotifications.headers.error',
            message: 'toastNotifications.messages.documentError',
            type: 'warning',
            component: ToastNotificationComponent
          });
        }
      };

      xhr.send();
    }

    this.generatingLinkPreloader = false;
    this.renderer.removeClass(document.body, MODAL_BODY_CLASS);
  }

  public toggleFiltration(): void {
    this.phoneFiltersToggled = !this.phoneFiltersToggled;
  }

  public selectCategory(category: AvailableUiCategories): void {
    this.categorySelected = category;
    this.loadDynamicDocuments(true);
  }

  public ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }
}
